<template>
  <div :id="id" class="modal fade">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <modal-close-button @confirmed="closeModal" />
          <h4 class="modal-title">Datenschutz-Anfrage stellen</h4>
        </div>

        <div class="modal-body">
          <!-- Loading -->
          <div v-if="ladeAktiv" class="text-center py-4">
            <b-spinner small /> Lade Analyse…
          </div>

          <template v-if="!ladeAktiv && analyse">
            <!-- Person info -->
            <div class="alert alert-info">
              <strong>{{ analyse.personName }}</strong>
              <span v-if="analyse.bereitsAnonymisiert" class="badge badge-warning ml-2">Bereits anonymisiert</span>
            </div>

            <!-- Retention info -->
            <div class="form-group">
              <label class="font-weight-bold">Aufbewahrungsfrist</label>
              <div>
                <span v-if="analyse.fruehestensLoeschenAm">
                  Frühestes Löschdatum: <strong>{{ analyse.fruehestensLoeschenAm }}</strong>
                </span>
                <span v-else class="text-muted">Keine gesetzliche Frist ermittelt</span>
              </div>
              <ul v-if="analyse.aufbewahrungspflichten && analyse.aufbewahrungspflichten.length > 0" class="mt-2 mb-0 pl-3">
                <li v-for="(hinweis, i) in analyse.aufbewahrungspflichten" :key="i" class="text-muted" style="font-size:0.875rem">
                  {{ hinweis.beschreibung }}: {{ hinweis.aufbewahrungBis }}
                </li>
              </ul>
            </div>

            <!-- Betroffene Tabellen -->
            <div v-if="analyse.betroffeneTabellen && analyse.betroffeneTabellen.length > 0" class="form-group">
              <label class="font-weight-bold">Betroffene Daten</label>
              <ul class="mb-0 pl-3">
                <li
                  v-for="(t, i) in analyse.betroffeneTabellen"
                  :key="i"
                  class="text-muted"
                  style="font-size:0.875rem"
                >
                  {{ t.beschreibung }}
                  <span v-if="t.anzahlDatensaetze">({{ t.anzahlDatensaetze }} Einträge)</span>
                </li>
              </ul>
            </div>

            <hr />

            <!-- AHV-Nummer — erforderlich wenn keine AHV im System -->
            <div v-if="ahvNummerErforderlich" class="form-group">
              <label>AHV-Nummer <span class="text-danger">*</span></label>
              <input
                v-model="ahvNummer"
                type="text"
                class="form-control"
                placeholder="756.XXXX.XXXX.XX"
                autocomplete="off"
              />
              <small class="form-text text-warning">
                <font-awesome-icon icon="fa-solid fa-triangle-exclamation" class="mr-1" />
                Die Person hat keine AHV-Nummer im System. Für das Auskunftsrecht (nDSG Art. 25) wird
                sie zur Berechnung der Pseudonym-UUID benötigt — ohne sie kann die Anonymisierung nicht
                durchgeführt werden. Die Nummer wird nicht gespeichert.
              </small>
            </div>

            <!-- Grund input -->
            <div class="form-group">
              <label>Begründung <span class="text-danger">*</span></label>
              <textarea
                v-model="grund"
                class="form-control"
                rows="3"
                placeholder="Warum sollen die Personendaten gelöscht werden? (z.B. Widerruf der Einwilligung, Art. 32 nDSG)"
              />
              <small class="form-text text-muted">Pflichtfeld — wird im Datenschutz-Protokoll gespeichert.</small>
            </div>

            <div v-if="fehler" class="alert alert-danger">{{ fehler }}</div>
          </template>
        </div>

        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeModal">Abbrechen</button>
          <button
            class="btn btn-danger"
            :disabled="ladeAktiv || !grund.trim() || sendeAktiv || (ahvNummerErforderlich && !ahvNummer.trim())"
            @click="bestaetigen"
          >
            <b-spinner v-if="sendeAktiv" small class="mr-1" />
            <font-awesome-icon v-else icon="fa-regular fa-xmark" class="mr-1" />
            Löschung beantragen
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppApi from "@/api/AppApi";
import { apiErrorToAlert } from "@/utils/Errorhandler";
import ModalCloseButton from "@/components/global/ModalCloseButton";
import { modal } from "@/mixins/Modal";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
library.add(faXmark, faTriangleExclamation);

const DATENSCHUTZ_ANALYSE_PATH   = "datenschutz/analyse";
const DATENSCHUTZ_LOESCHUNG_PATH = "datenschutz/loeschung";

export default {
  name: "DatenschutzAnfrageModal",
  components: { ModalCloseButton },
  mixins: [modal],
  props: {
    personId: { type: String, default: null },
    personName: { type: String, default: "" },
  },
  data() {
    return {
      analyse: null,
      grund: "",
      ahvNummer: "",
      ladeAktiv: false,
      sendeAktiv: false,
      fehler: null,
    };
  },
  computed: {
    ahvNummerErforderlich() {
      return this.analyse
        && !this.analyse.hatAhvNummer
        && !this.analyse.bereitsAnonymisiert;
    },
  },
  methods: {
    oeffnen() {
      this.grund = "";
      this.ahvNummer = "";
      this.fehler = null;
      this.analyse = null;
      this.ladeAnalyse();
      this.openModal();
    },
    async ladeAnalyse() {
      if (!this.personId) return;
      this.ladeAktiv = true;
      this.analyse = null;
      this.fehler = null;
      try {
        const resp = await AppApi.get(`${DATENSCHUTZ_ANALYSE_PATH}/`, { params: { personId: this.personId } });
        this.analyse = resp.data;
      } catch (e) {
        this.fehler = "Analyse konnte nicht geladen werden.";
      } finally {
        this.ladeAktiv = false;
      }
    },
    async bestaetigen() {
      if (!this.grund.trim()) return;
      this.sendeAktiv = true;
      this.fehler = null;
      try {
        await AppApi.post(`${DATENSCHUTZ_LOESCHUNG_PATH}/`, {
          personId: this.personId,
          grund: this.grund.trim(),
          ahvNummer: this.ahvNummerErforderlich ? this.ahvNummer.trim() : null,
        });
        this.$notify({ type: "success", title: "Anfrage gestellt", text: "Die Datenschutz-Anfrage wurde erfasst." });
        this.$emit("anfrage-gestellt");
        this.closeModal();
      } catch (e) {
        this.fehler = "Anfrage konnte nicht gesendet werden.";
        this.$notify(apiErrorToAlert(e));
      } finally {
        this.sendeAktiv = false;
      }
    },
  },
};
</script>
